// cartContext.tsx
import { Product } from 'models';
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ProductCartContextType, ProductCartItem } from 'types/ProductType';

const CartContext = createContext<ProductCartContextType | undefined>(undefined);

// Custom hook for cart operations
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart Provider component
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<ProductCartItem[]>([]);

  // Check if a product is in cart
  const isInCart = useCallback(
    (productId: string) => {
      return cartItems.some((item) => item.productId === productId);
    },
    [cartItems],
  );

  // Find cart item quantity
  const getItemQuantity = useCallback(
    (productId: string) => {
      const item = cartItems.find((item) => item.productId === productId);
      return item ? item.quantity : 0;
    },
    [cartItems],
  );
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);
  // Calculate total price for cart
  const getCartTotal = useCallback(
    (products: Product[]) => {
      console.log(products);
      return cartItems.reduce((total, item) => {
        // const product = products.find((p) => p.id === item.productId);
        return total + (item.costPrice ? (item?.costPrice || 0) * item.quantity : 0);
      }, 0);
    },
    [cartItems],
  );

  // Add to cart handler
  const addToCart = useCallback((productId: string, salePrice: number, costPrice: number) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.productId === productId);

      if (existingItem) {
        return prevItems.map((item) =>
          item.productId === productId ? { ...item, quantity: item.quantity + 1, salePrice, costPrice } : item,
        );
      } else {
        return [...prevItems, { productId, quantity: 1, salePrice, costPrice }];
      }
    });
  }, []);

  // Update quantity handler
  const updateQuantity = useCallback((productId: string, quantity: number, salePrice?: number, costPrice?: number) => {
    setCartItems((prevItems) => {
      if (quantity <= 0) {
        return prevItems.filter((item) => item.productId !== productId);
      } else {
        const existingItem = prevItems.find((item) => item.productId === productId);

        if (existingItem) {
          return prevItems.map((item) => (item.productId === productId ? { ...item, quantity } : item));
        } else if (quantity > 0) {
          return [...prevItems, { productId, quantity, salePrice, costPrice }];
        }
        return prevItems;
      }
    });
  }, []);
  //update salePrice and costPrice
  const updateProductPrice = useCallback((productId: string, prices: { salePrice: number; costPrice: number }) => {
    setCartItems((prevItems) => {
      return prevItems.map((item) => (item.productId === productId ? { ...item, ...prices } : item));
    });
  }, []);

  const initializeCartItems = useCallback(
    (itemsFromAPI: { productVersionId: string; quantity: number; salePrice: number; costPrice: number }[]) => {
      const convertedItems: ProductCartItem[] = itemsFromAPI.map((item) => ({
        productId: item.productVersionId,
        quantity: item.quantity,
        salePrice: item.salePrice,
        costPrice: item.costPrice,
      }));
      setCartItems(convertedItems);
    },
    [],
  );

  const value = {
    cartItems,
    addToCart,
    updateQuantity,
    isInCart,
    getItemQuantity,
    getCartTotal,
    clearCart,
    initializeCartItems,
    updateProductPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
